import { useEffect, useMemo } from 'react';

import { meshModules } from 'Deer/constants/modules';
import { L3_PATHS } from 'Deer/constants/routes';
import { getUserAccess } from 'Deer/helpers/getUserAccess';
import { useRouteToMyTeamLevels, useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { IntentFilters } from 'Deer/intent/components/Layout/IntentFilters.layout';
import { IntentBreadcrumbType } from 'Deer/intent/types/IntentCommonTypes';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';
import { useOKRManageViewType } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/useOKRManageViewType';
import { checkEngagementModuleEnabled } from 'utils/helpers/moduleAccess';
import { checkUserIsEngagementAdmin } from 'utils/helpers/rolesAccess';

import { ENGAGEMENT_INTENTS } from 'constants/moduleIntentTypes';

import { AgGridEllipsisMenu } from 'uiToolkitV1/AgGridWrapperV2/components/EllipsisMenu/AgGridEllipsisMenu';

import { useEngagementReportIntentEllipsisValues } from '../hooks/useEngagementReportIntentEllipsisValues';
import { EngagementIntentType, setSelectedEngagementIntent } from '../slice/EngagementIntentSlice';
import { EngagementMetaFilters } from './EngagementMetaFilters';

// import { useOKREllipsisValues } from '../hooks/useOKREllipsisValues';
// import { useOKRGroupByValues } from '../hooks/useOKRGroupByValues';
// import { useOKRViewTypeValues } from '../hooks/useOKRViewTypeValues';
// import { GoalsIntentType, setSelectedGoalsIntent } from '../slice/GoalsIntentSlice';
// import { MetaFilters } from './MetaFilters';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EngagementIntentDropdown = ({
  gridRef,
  showKebabMenu = true,
  timelineDropdownOnChangeCallback,
  ownershipDropdownOnChangeCallback,
  breadcrumbs,
  disableMetaFilters = false,
  disableEllipsisMenu = false,
}: {
  gridRef?: unknown;
  showKebabMenu?: boolean;
  disableMetaFilters?: boolean;
  disableEllipsisMenu?: boolean;
  timelineDropdownOnChangeCallback?: (onChange: () => void) => void;
  ownershipDropdownOnChangeCallback?: (onChange: () => void) => void;
  breadcrumbs: IntentBreadcrumbType;
}) => {
  const { l1Module } = useRouteToNavLevels();
  const { teamType } = useRouteToMyTeamLevels();
  const history = useHistory();
  const { pathname, search } = useLocation();
  const dispatch = useDispatch();
  const sideNavSelectedCohort = useSelectedView();
  const selectedCohortId = sideNavSelectedCohort?.id;
  const { viewType: OKRManageView } = useOKRManageViewType();
  const userAccess = getUserAccess();
  const ellipsisMenuOptions = useEngagementReportIntentEllipsisValues({
    gridRef,
  });

  const engagementTypeFromQueryParams = new URLSearchParams(search).get('surveyType');

  // eslint-disable-next-line arrow-body-style
  const intentDropdownOptions = useMemo<Array<EngagementIntentType>>(() => {
    return [
      {
        id: ENGAGEMENT_INTENTS.ENGAGEMENT_PENDING_ON_ME,
        label: 'Pending on me',
        onClick: () => {
          const values = {
            myData: L3_PATHS['my-data'].engagement.mySurveys,
          };

          const queryParams = new URLSearchParams(search);
          queryParams.set('surveyType', 'ACTIVE_SURVEYS');

          history.push({
            pathname: values.myData,
            search: queryParams.toString(),
          });
        },
        visible: () => l1Module === 'myData',
        value: {
          myData: {
            path: L3_PATHS['my-data'].engagement.mySurveys,
            isAccessible: checkEngagementModuleEnabled,
          },
        },
        queryParamValue: 'ACTIVE_SURVEYS',
        category: 'Manage',
        isDefault: true,
        showTimelineDropdown: false,
        showSplitByDropdown: false,
        showSurveyDropdown: false,
        hasMetaFilters: false,
      },
      {
        id: ENGAGEMENT_INTENTS.ENGAGEMENT_COMPLETED_SURVEYS,
        label: 'Completed surveys',
        onClick: () => {
          const values = {
            myData: L3_PATHS['my-data'].engagement.mySurveys,
          };

          const queryParams = new URLSearchParams(search);
          queryParams.set('surveyType', 'SUBMITTED_SURVEYS');

          history.push({
            pathname: values.myData,
            search: queryParams.toString(),
          });
        },
        visible: () => l1Module === 'myData',
        value: {
          myData: {
            path: L3_PATHS['my-data'].engagement.mySurveys,
            isAccessible: checkEngagementModuleEnabled,
          },
        },
        queryParamValue: 'SUBMITTED_SURVEYS',
        category: 'Manage',
        isDefault: false,
        showTimelineDropdown: false,
        showSplitByDropdown: false,
        showSurveyDropdown: false,
        hasMetaFilters: false,
      },
      {
        id: ENGAGEMENT_INTENTS.ENGAGEMENT_REPORTS,
        label: 'Reports',
        onClick: () => {
          const values = {
            team: {
              directReports: L3_PATHS.team.directReports.engagement.insights,
              allReports: L3_PATHS.team.allReports.engagement.insights,
            },
            company: L3_PATHS.company.engagement.insights,
          };

          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : values[l1Module];
          history.push({
            pathname: urlToPush,
          });
        },
        visible: () => l1Module !== 'myData',
        value: {
          team: {
            directReports: {
              path: L3_PATHS.team.directReports.engagement.insights,
              isAccessible: checkEngagementModuleEnabled,
            },
            allReports: {
              path: L3_PATHS.team.allReports.engagement.insights,
              isAccessible: checkEngagementModuleEnabled,
            },
          },
          company: {
            path: L3_PATHS.company.engagement.insights,
            isAccessible: () => checkUserIsEngagementAdmin() || getUserAccess()?.isCustomEngagementAdmin,
          },
        },
        category: 'Analyze',
        isDefault: true,
        showTimelineDropdown: true,
        showSplitByDropdown: true,
        showViewByDropdown: true,
        showSurveyDropdown: true,
        hasMetaFilters: true,
      },
      {
        id: ENGAGEMENT_INTENTS.ENGAGEMENT_SETTINGS,
        label: 'Settings',
        onClick: () => {
          const values = {
            company: L3_PATHS.company.engagement.settings,
          };

          history.push({
            pathname: values.company,
          });
        },
        visible: () => l1Module !== 'myData' && l1Module !== 'team',
        value: {
          company: {
            path: L3_PATHS.company.engagement.settings,
            isAccessible: () => checkUserIsEngagementAdmin(),
          },
        },
        category: 'Configure',
        isDefault: false,
        showTimelineDropdown: false,
        showSplitByDropdown: false,
        showSurveyDropdown: false,
        hasMetaFilters: false,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCohortId, OKRManageView, l1Module, teamType, history, userAccess?.isCustomGoalAdmin]);

  const selectedIntent = useMemo(
    () =>
      intentDropdownOptions.find((option) => {
        // for query param based intent options such as active and archived 1:1s
        if (option.queryParamValue) {
          return (
            engagementTypeFromQueryParams === option.queryParamValue &&
            (pathname.includes(option.value.myData.path) || pathname.includes(option.value.team?.[teamType].path))
          );
        }
        const isTeamType = l1Module === 'team';
        if (isTeamType) {
          return pathname.includes(option.value.team?.[teamType]?.path);
        }
        return pathname.includes(option.value[l1Module]?.path);
      }),
    [intentDropdownOptions, l1Module, pathname, engagementTypeFromQueryParams, teamType]
  );

  useEffect(() => {
    // if selectedIntent changes then update it in redux
    dispatch(setSelectedEngagementIntent(selectedIntent));
  }, [selectedIntent, dispatch]);

  return (
    <IntentFilters
      intentDropdownOptions={intentDropdownOptions.filter((option) => option.visible())}
      selectedIntent={selectedIntent?.label ?? ''}
      selectedIntentId={selectedIntent?.id ?? ''}
      KebabMenu={
        // @ts-expect-error options type error
        <AgGridEllipsisMenu
          options={ellipsisMenuOptions}
          selectedRadioValue="showArchivedGoals"
          disableEllipsisMenu={disableEllipsisMenu}
        />
      }
      MetaFilters={<EngagementMetaFilters />}
      showKebabMenu={showKebabMenu}
      moduleName={meshModules.engagement.value}
      intentTitle={meshModules.engagement.label}
      isCategorized
      breadcrumbs={breadcrumbs}
    />
  );
};

export { EngagementIntentDropdown };
